import { useState } from "react";
import { ChevronLeft, ChevronRight, Plus, X } from "lucide-react";
import { useCollection } from "@/hooks/useCollection";
import { PageHeader, PageSkeleton } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatDate, toDateStr } from "@/lib/format";
import { toast } from "sonner";

const CATS = ["Appointment", "Meeting", "Tasting", "Fitting", "Payment", "RSVP Deadline", "Event", "Other"];

export default function CalendarPage() {
  const { items, loading, create, remove } = useCollection("events");
  const [cursor, setCursor] = useState(new Date());
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [selected, setSelected] = useState(null);

  if (loading) return <PageSkeleton />;

  const year = cursor.getFullYear(), month = cursor.getMonth();
  const first = new Date(year, month, 1);
  const startDay = first.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const todayStr = toDateStr(new Date());
  const monthName = cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" });

  const eventsOn = (dateStr) => items.filter((e) => (e.date || "").slice(0, 10) === dateStr);

  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(toDateStr(new Date(year, month, d)));

  const save = async () => {
    const payload = { title: (form.title || "").trim(), date: form.date || todayStr, start_time: form.start_time || "",
      end_time: form.end_time || "", category: form.category || "Event", location: form.location || "", notes: form.notes || "" };
    if (!payload.title) { toast.error("Please add a title."); return; }
    await create(payload); toast.success("Event added"); setSelected(payload.date); setOpen(false); setForm({});
  };

  return (
    <div>
      <PageHeader title="Calendar" subtitle="Appointments, payments and deadlines at a glance." testId="calendar-header"
        action={<Button onClick={() => { setForm({ date: selected || todayStr }); setOpen(true); }} className="rounded-full" data-testid="add-event"><Plus className="h-4 w-4 mr-1.5" />Add Event</Button>} />

      <div className="rounded-2xl bg-card border border-border overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h3 className="text-xl tracking-tight text-primary">{monthName}</h3>
          <div className="flex gap-1">
            <Button variant="outline" size="icon" className="rounded-full h-9 w-9" onClick={() => setCursor(new Date(year, month - 1, 1))} data-testid="prev-month"><ChevronLeft className="h-4 w-4" /></Button>
            <Button variant="outline" className="rounded-full h-9" onClick={() => setCursor(new Date())} data-testid="today-btn">Today</Button>
            <Button variant="outline" size="icon" className="rounded-full h-9 w-9" onClick={() => setCursor(new Date(year, month + 1, 1))} data-testid="next-month"><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
        <div className="grid grid-cols-7 text-center text-xs text-muted-foreground border-b border-border">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d} className="py-2 font-medium">{d}</div>)}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((dateStr, i) => {
            if (!dateStr) return <div key={`blank-${i}`} className="min-h-[92px] border-b border-r border-border bg-secondary/20" />;
            const evs = eventsOn(dateStr);
            const isToday = dateStr === todayStr;
            return (
              <button key={dateStr} onClick={() => setSelected(dateStr)} data-testid="calendar-day"
                className="min-h-[92px] border-b border-r border-border p-1.5 text-left hover:bg-secondary/40 transition-colors align-top">
                <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs tnum ${isToday ? "bg-primary text-primary-foreground" : "text-foreground"}`}>{Number(dateStr.slice(8, 10))}</span>
                <div className="mt-1 space-y-0.5">
                  {evs.slice(0, 3).map((e) => <div key={e.id} className="text-[10px] truncate rounded bg-accent/20 text-accent-foreground px-1 py-0.5">{e.start_time ? e.start_time + " " : ""}{e.title}</div>)}
                  {evs.length > 3 && <div className="text-[10px] text-muted-foreground">+{evs.length - 3} more</div>}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Day detail */}
      {selected && (
        <div className="rounded-2xl bg-card border border-border p-6 mt-4" data-testid="day-detail">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl tracking-tight text-primary">{formatDate(selected, { weekday: "long", day: "numeric", month: "long" })}</h3>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="rounded-full" onClick={() => { setForm({ date: selected }); setOpen(true); }}><Plus className="h-3.5 w-3.5 mr-1" />Add</Button>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setSelected(null)}><X className="h-4 w-4" /></Button>
            </div>
          </div>
          {eventsOn(selected).length ? (
            <div className="space-y-2">
              {eventsOn(selected).map((e) => (
                <div key={e.id} className="flex items-center justify-between rounded-xl bg-secondary/40 px-4 py-3">
                  <div><div className="font-medium">{e.title}</div><div className="text-xs text-muted-foreground">{[e.start_time, e.category, e.location].filter(Boolean).join(" · ")}</div></div>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => { remove(e.id); toast.success("Event deleted"); }} data-testid="delete-event"><X className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-muted-foreground">No events this day.</p>}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="text-2xl">Add event</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="col-span-2"><Label className="text-xs text-muted-foreground">Title</Label><Input value={form.title || ""} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} className="rounded-xl mt-1" data-testid="event-title" /></div>
            <div><Label className="text-xs text-muted-foreground">Date</Label><Input type="date" value={form.date || ""} onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))} className="rounded-xl mt-1" data-testid="event-date" /></div>
            <div><Label className="text-xs text-muted-foreground">Category</Label>
              <Select value={form.category || "Event"} onValueChange={(v) => setForm((p) => ({ ...p, category: v }))}><SelectTrigger className="rounded-xl mt-1"><SelectValue /></SelectTrigger><SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-xs text-muted-foreground">Start</Label><Input type="time" value={form.start_time || ""} onChange={(e) => setForm((p) => ({ ...p, start_time: e.target.value }))} className="rounded-xl mt-1" /></div>
            <div><Label className="text-xs text-muted-foreground">End</Label><Input type="time" value={form.end_time || ""} onChange={(e) => setForm((p) => ({ ...p, end_time: e.target.value }))} className="rounded-xl mt-1" /></div>
            <div className="col-span-2"><Label className="text-xs text-muted-foreground">Location</Label><Input value={form.location || ""} onChange={(e) => setForm((p) => ({ ...p, location: e.target.value }))} className="rounded-xl mt-1" /></div>
          </div>
          <DialogFooter><Button variant="outline" className="rounded-full" onClick={() => setOpen(false)}>Cancel</Button><Button className="rounded-full" onClick={save} data-testid="save-event">Add</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
